import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatCount, formatDate } from '../../lib/format.js';
import { useAdminBalances, useAdjustStock, useAdminWarehouses } from '../hooks/use-inventory.js';
import { StockAdjustmentDialog } from '../components/stock-adjustment-dialog.js';
import type { AdminBalance, MovementType } from '../types/inventory.js';

function stockTone(balance: AdminBalance): 'success' | 'warning' | 'neutral' {
  const available = balance.quantityAvailable ?? 0;
  if (available <= 0) return 'warning';
  if (available <= balance.lowStockThreshold) return 'warning';
  return 'success';
}

function stockLabel(balance: AdminBalance): string {
  const available = balance.quantityAvailable ?? 0;
  if (available <= 0) return 'Out of stock';
  if (available <= balance.lowStockThreshold) return 'Low stock';
  return 'In stock';
}

export function InventoryPage() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [warehouseId, setWarehouseId] = useState('');
  const [stockStatus, setStockStatus] = useState('');
  const [adjustingBalance, setAdjustingBalance] = useState<AdminBalance | null>(null);

  const { data: warehouses } = useAdminWarehouses();
  const { data, isLoading, isError, refetch, isFetching } = useAdminBalances({
    search: search || undefined,
    warehouseId: warehouseId || undefined,
    stockStatus: (stockStatus || undefined) as 'low' | 'out' | undefined,
    limit: 50,
  });
  const adjustStock = useAdjustStock();

  const rows = data ?? [];

  const columns: AdminDataTableColumn<AdminBalance>[] = [
    {
      key: 'productName',
      header: 'Product',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.productName}</p>
          <p className="text-xs text-gray-400">
            {row.variantName} · SKU {row.sku}
          </p>
        </div>
      ),
    },
    { key: 'warehouseCode', header: 'Warehouse', render: (row) => row.warehouseCode },
    { key: 'quantityOnHand', header: 'On hand', render: (row) => formatCount(row.quantityOnHand) },
    { key: 'quantityReserved', header: 'Reserved', render: (row) => formatCount(row.quantityReserved) },
    {
      key: 'quantityAvailable',
      header: 'Available',
      render: (row) => formatCount(row.quantityAvailable ?? 0),
    },
    { key: 'lowStockThreshold', header: 'Threshold', render: (row) => formatCount(row.lowStockThreshold) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <AdminStatusBadge label={stockLabel(row)} tone={stockTone(row)} />,
    },
    { key: 'updatedAt', header: 'Last updated', render: (row) => formatDate(row.updatedAt) },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Inventory"
        description="Stock balances across every warehouse."
        breadcrumbs={[{ label: 'Inventory' }]}
        actions={
          <>
            <AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />
            <Link
              to={ROUTE_NAMES.adminInventoryMovements}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Movement history
            </Link>
          </>
        }
      />

      <AdminSectionCard title="Stock balances" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search product, variant, or SKU…"
            label="Search inventory"
          />
          <AdminSelect
            label="Warehouse"
            value={warehouseId}
            onChange={setWarehouseId}
            allLabel="All warehouses"
            options={(warehouses ?? []).map((w) => ({ value: w.id, label: w.name }))}
          />
          <AdminSelect
            label="Stock status"
            value={stockStatus}
            onChange={setStockStatus}
            allLabel="Any stock level"
            options={[
              { value: 'low', label: 'Low stock' },
              { value: 'out', label: 'Out of stock' },
            ]}
          />
        </AdminFilterBar>

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No matching stock balances"
          rowActions={(row) => (
            <button
              type="button"
              onClick={() => setAdjustingBalance(row)}
              className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
            >
              Adjust
            </button>
          )}
        />
      </AdminSectionCard>

      <StockAdjustmentDialog
        balance={adjustingBalance}
        isSubmitting={adjustStock.isPending}
        onClose={() => setAdjustingBalance(null)}
        onSubmit={(input) => {
          if (!adjustingBalance) return;
          adjustStock.mutate(
            {
              variantId: adjustingBalance.variantId,
              warehouseId: adjustingBalance.warehouseId,
              movementType: input.movementType as MovementType,
              quantity: input.quantity,
              note: input.note,
            },
            { onSuccess: () => setAdjustingBalance(null) },
          );
        }}
      />
    </AdminContent>
  );
}
